import { Component, inject, OnInit, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import {
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormControl,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCheckboxModule } from '@angular/material/checkbox';
import moment from 'moment';

import { SmartOrpcClientService } from '../../../shared/services/smart-orpc-client.service';
import { SnackbarService } from '../../../shared/services/snackbar.service';
import type { IOrganization } from '#backend/common/organization-types';
import { ContractModality } from '#backend/common/contract-types';

interface Contract {
  contractNumber: string;
  organizationId: number | null;
  startDate: Date | null;
  endDate: Date | null;
  modality: ContractModality;
  complianceReportsAmount: number | null;
  historyReportsAmount: number | null;
  monitoringHectaresAmount: number | null;
}

@Component({
  selector: 'app-contract-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatCheckboxModule,
  ],
  templateUrl: './contract-form.component.html',
  styleUrls: ['./contract-form.component.scss'],
})
export class ContractFormComponent implements OnInit {
  private orpc = inject(SmartOrpcClientService).client;
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private snackBar = inject(SnackbarService);

  contractForm = new FormGroup({
    contractNumber: new FormControl('', {
      validators: [Validators.required, Validators.maxLength(100)],
      nonNullable: true,
    }),
    organizationId: new FormControl<number | null>(null, {
      validators: Validators.required,
      nonNullable: true,
    }),
    startDate: new FormControl<Date | null>(null, Validators.required),
    endDate: new FormControl<Date | null>(null, Validators.required),
    modality: new FormControl<ContractModality>(
      ContractModality.WITH_COMMITMENT,
      {
        validators: Validators.required,
        nonNullable: true,
      },
    ),
    complianceReportsAmount: new FormControl<number | null>(null, [
      Validators.min(0),
    ]),
    historyReportsAmount: new FormControl<number | null>(null, [
      Validators.min(0),
    ]),
    monitoringHectaresAmount: new FormControl<number | null>(null, [
      Validators.min(0),
    ]),
  });

  organizations: IOrganization[] = [];
  contractId?: number;
  isEditMode = false;
  isLoading = false;
  isLoadingData = true;

  initialOrganizationId = input<number>();
  id = input<string>();

  modalityOptions: { value: ContractModality; label: string }[] = [
    {
      value: ContractModality.WITH_COMMITMENT,
      label: 'Com compromisso de consumo',
    },
    {
      value: ContractModality.WITHOUT_COMMITMENT,
      label: 'Sem compromisso de consumo',
    },
  ];

  ngOnInit(): void {
    this.initializeForm();
    this.loadInitialData();

    if (this.id()) {
      this.contractId = Number(this.id());
      this.isEditMode = true;
    }

    // Check for organizationId in query params
    const organizationId = this.route.snapshot.queryParams['organizationId'];
    if (organizationId) {
      this.contractForm.patchValue({
        organizationId: parseInt(organizationId, 10),
      });
    }

    // Watch modality changes
    this.contractForm.controls.modality.valueChanges.subscribe((modality) => {
      this.handleModalityChange(modality);
    });
  }

  initializeForm(): void {
    const orgId = this.initialOrganizationId();
    if (orgId) {
      this.contractForm.controls.organizationId.setValue(orgId);
    }
  }

  async loadInitialData(): Promise<void> {
    try {
      await this.loadOrganizations();

      if (this.isEditMode && this.contractId) {
        await this.loadContract(this.contractId);
      }

      this.isLoadingData = false;
    } catch (error) {
      console.error('Error loading initial data:', error);
      this.snackBar.alert('Erro ao carregar dados iniciais');
      this.isLoadingData = false;
    }
  }

  handleModalityChange(modality: ContractModality): void {
    const controls = this.contractForm.controls;

    if (modality === ContractModality.WITH_COMMITMENT) {
      // With commitment: user specifies exact quantities for each product
      controls.historyReportsAmount.addValidators([Validators.required]);
      controls.complianceReportsAmount.addValidators([Validators.required]);
      controls.monitoringHectaresAmount.addValidators([Validators.required]);
      controls.historyReportsAmount.setValue(0);
      controls.complianceReportsAmount.setValue(0);
      controls.monitoringHectaresAmount.setValue(0);
    } else {
      // Without commitment: user chooses which products are available (unlimited)
      // Reset to null (unlimited) when products are selected, or disabled when not
      controls.historyReportsAmount.removeValidators(Validators.required);
      controls.complianceReportsAmount.removeValidators(Validators.required);
      controls.monitoringHectaresAmount.removeValidators(Validators.required);
      controls.historyReportsAmount.setValue(null);
      controls.complianceReportsAmount.setValue(null);
      controls.monitoringHectaresAmount.setValue(null);
    }
  }

  async loadOrganizations(): Promise<void> {
    try {
      this.organizations = (await this.orpc.organization.list({})).items;
    } catch (error) {
      console.error('Error loading organizations:', error);
      throw error;
    }
  }

  async loadContract(id: number): Promise<void> {
    try {
      const contract = await this.orpc.contract.byId(id);
      if (contract) {
        const {
          complianceReportsLimit: complianceReportsAmount,
          historyReportsLimit: historyReportsAmount,
          monitoringHectaresLimit: monitoringHectaresAmount,
        } = contract;
        this.contractForm.patchValue({
          contractNumber: contract.contractNumber,
          organizationId: contract.organizationId,
          startDate: new Date(contract.startDate + 'T00:00:00-03:00'),
          endDate: new Date(contract.endDate + 'T00:00:00-03:00'),
          modality: contract.modality,
          complianceReportsAmount,
          historyReportsAmount,
          monitoringHectaresAmount,
        });
      }
    } catch (error) {
      console.error('Error loading contract:', error);
      throw error;
    }
  }

  async onSubmit(): Promise<void> {
    if (this.contractForm.valid && !this.isLoading) {
      this.isLoading = true;

      try {
        const formValue = this.contractForm.getRawValue() as Contract;
        const dateString = (d: Date) => d?.toISOString().split('T')[0]; //YYYY-MM-DD
        const validProduct = this.validProduct(formValue);
        if (!validProduct) {
          this.snackBar.alert(
            'Por favor, selecione pelo menos um produto contratado conforme a modalidade do contrato.',
          );
          this.isLoading = false;
          return;
        }
        if (this.isEditMode) {
          await this.orpc.contract.update({
            ...formValue,
            id: this.contractId!,
            organizationId: formValue.organizationId!,
            startDate: dateString(formValue.startDate!),
            endDate: dateString(formValue.endDate!),
          });
          this.snackBar.alert('Contrato atualizado com sucesso!');
        } else {
          await this.orpc.contract.create({
            ...formValue,
            organizationId: formValue.organizationId!,
            startDate: dateString(formValue.startDate!),
            endDate: dateString(formValue.endDate!),
          });
          this.snackBar.alert('Contrato cadastrado com sucesso!');
        }

        this.router.navigate(['/admin/contracts']);
      } catch (error) {
        console.error('Error saving contract:', error);
        this.snackBar.alert('Erro ao salvar contrato');
      } finally {
        this.isLoading = false;
      }
    } else {
      this.markFormGroupTouched();
      this.snackBar.alert('Por favor, preencha todos os campos obrigatórios');
    }
  }
  validProduct(formValue: Contract) {
    // Check if at least one product is selected based on modality
    if (formValue.modality === ContractModality.WITHOUT_COMMITMENT) {
      // For without commitment, check if at least one product is enabled (amount is null)
      return (
        formValue.complianceReportsAmount == null ||
        formValue.historyReportsAmount == null ||
        formValue.monitoringHectaresAmount == null
      );
    }

    if (formValue.modality === ContractModality.WITH_COMMITMENT) {
      return (
        (formValue.complianceReportsAmount != null &&
          formValue.complianceReportsAmount > 0) ||
        (formValue.historyReportsAmount != null &&
          formValue.historyReportsAmount > 0) ||
        (formValue.monitoringHectaresAmount != null &&
          formValue.monitoringHectaresAmount > 0)
      );
    }

    return false;
  }

  onCancel(): void {
    this.router.navigate(['/admin/contracts']);
  }

  getOrganizationName(organizationId: number): string {
    const organization = this.organizations.find(
      (o) => o.id === organizationId,
    );
    return organization ? organization.name : '';
  }

  private markFormGroupTouched(): void {
    Object.keys(this.contractForm.controls).forEach((key) => {
      const control = this.contractForm.get(key);
      control?.markAsTouched();
    });
  }

  hasError(controlName: string, errorType: string): boolean {
    const control = this.contractForm.get(controlName);
    return !!(control && control.hasError(errorType) && control.touched);
  }

  getErrorMessage(controlName: string): string {
    const control = this.contractForm.get(controlName);
    if (control && control.errors && control.touched) {
      if (control.errors['required']) return 'Este campo é obrigatório';
      if (control.errors['maxlength'])
        return `Máximo ${control.errors['maxlength'].requiredLength} caracteres`;
      if (control.errors['min'])
        return `Valor mínimo: ${control.errors['min'].min}`;
    }
    return '';
  }

  validDate() {
    const dateInitControl = this.contractForm.get('startDate');
    const dateEndControl = this.contractForm.get('endDate');
    const dateInit = dateInitControl?.value;
    const dateEnd = dateEndControl?.value;

    if (dateInit && dateEnd) {
      if (moment(dateInit).isAfter(dateEnd)) {
        dateEndControl?.setErrors({ dateInvalid: true });
        return false;
      } else {
        // Only clear the dateInvalid error, preserve other errors
        const errors = dateEndControl?.errors;
        if (errors && errors['dateInvalid']) {
          delete errors['dateInvalid'];
          dateEndControl?.setErrors(
            Object.keys(errors).length > 0 ? errors : null,
          );
        }
        return true;
      }
    }
    return true;
  }

  onDateChange(): void {
    // Trigger validation when date changes (both manual input and datepicker)
    this.validDate();
  }

  onDateBlur(controlName: 'startDate' | 'endDate'): void {
    // Mark as touched when user leaves the field
    const control = this.contractForm.get(controlName);
    if (control) {
      control.markAsTouched();
      control.updateValueAndValidity();
    }
    this.validDate();
  }
}
